/** Admin — assign a Retell voice agent to a business, behind the scenes.
 *  Clients never see the agent list; they only see the agent assigned here. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneCall, Search, Loader2, Check, X, User as UserIcon, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

const AdminVoiceAgents = () => {
  const [hostQ, setHostQ] = useState("");
  const [hosts, setHosts] = useState([]);
  const [hostsLoading, setHostsLoading] = useState(false);
  const [selected, setSelected] = useState(null); // { business_id, name, email }

  const [agents, setAgents] = useState(null);
  const [agentQ, setAgentQ] = useState("");
  const [busy, setBusy] = useState(false);

  const [assignments, setAssignments] = useState([]);

  const searchHosts = useCallback(async (q) => {
    setHostsLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/admin/voice/hosts?q=${encodeURIComponent(q)}`, { headers });
      const d = await res.json();
      setHosts(d.hosts || []);
    } catch { toast.error("Couldn't search accounts"); } finally { setHostsLoading(false); }
  }, []);

  const loadAssignments = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/admin/voice/assignments`, { headers });
      const d = await res.json();
      setAssignments(d.assignments || []);
    } catch { /* noop */ }
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/admin/voice/agents`, { headers });
      const d = await res.json();
      setAgents(d.agents || []);
    } catch { toast.error("Couldn't load voice agents"); setAgents([]); }
  }, []);

  useEffect(() => { searchHosts(""); loadAssignments(); loadAgents(); }, [searchHosts, loadAssignments, loadAgents]);

  // debounce host search
  useEffect(() => {
    const t = setTimeout(() => searchHosts(hostQ), 300);
    return () => clearTimeout(t);
  }, [hostQ, searchHosts]);

  const assign = async (agent_id) => {
    if (!selected) return;
    setBusy(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const res = await fetch(`${API}/admin/voice/assign`, {
        method: "PUT", headers, body: JSON.stringify({ business_id: selected.business_id, agent_id }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      toast.success(`Assigned "${d.agent_name}" to ${selected.email}`);
      await Promise.all([loadAssignments(), searchHosts(hostQ)]);
      setSelected((s) => s ? { ...s, agent_id, agent_name: d.agent_name } : s);
    } catch { toast.error("Couldn't assign that agent"); } finally { setBusy(false); }
  };

  const unassign = async (business_id) => {
    setBusy(true);
    try {
      const headers = await authHeaders();
      await fetch(`${API}/admin/voice/assign/${business_id}`, { method: "DELETE", headers });
      toast.success("Assignment removed");
      await Promise.all([loadAssignments(), searchHosts(hostQ)]);
      setSelected((s) => s && s.business_id === business_id ? { ...s, agent_id: null, agent_name: null } : s);
    } catch { toast.error("Couldn't remove assignment"); } finally { setBusy(false); }
  };

  const filteredAgents = (agents || []).filter((a) =>
    !agentQ || (a.name || "").toLowerCase().includes(agentQ.toLowerCase()) || (a.agent_id || "").toLowerCase().includes(agentQ.toLowerCase())
  );

  return (
    <div data-testid="admin-voice-section">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2"><PhoneCall className="w-6 h-6" /> Voice Agents</h1>
        <p className="text-sm text-muted-foreground mt-1">Assign a Retell agent to a business. The client only ever sees the agent you assign here.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Step 1: pick a business */}
        <Card className="p-4">
          <h3 className="font-medium text-sm mb-3">1. Choose a business</h3>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={hostQ} onChange={(e) => setHostQ(e.target.value)} placeholder="Search by name or email…" className="pl-9" data-testid="admin-voice-host-search" />
          </div>
          <div className="divide-y divide-border max-h-80 overflow-y-auto" data-testid="admin-voice-host-list">
            {hostsLoading ? (
              <div className="py-8 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : hosts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No accounts found.</p>
            ) : hosts.map((h) => (
              <button
                key={h.business_id}
                onClick={() => setSelected(h)}
                className={`w-full flex items-center gap-3 py-2.5 px-2 text-left rounded-lg ${selected?.business_id === h.business_id ? "bg-muted" : "hover:bg-muted/50"}`}
                data-testid={`admin-voice-host-${h.business_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0"><UserIcon className="w-4 h-4 text-muted-foreground" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{h.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{h.email}</p>
                </div>
                {h.agent_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 shrink-0">{h.agent_name}</span>}
              </button>
            ))}
          </div>
        </Card>

        {/* Step 2: pick an agent */}
        <Card className="p-4">
          <h3 className="font-medium text-sm mb-1">2. Assign a voice agent</h3>
          {selected ? (
            <p className="text-xs text-muted-foreground mb-3">
              For <span className="font-medium text-foreground">{selected.email}</span>
              {selected.agent_name && <> · currently <span className="font-medium text-foreground">{selected.agent_name}</span>
                <button onClick={() => unassign(selected.business_id)} className="ml-2 text-destructive hover:underline" data-testid="admin-voice-unassign-selected">remove</button></>}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mb-3">Pick a business on the left first.</p>
          )}
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={agentQ} onChange={(e) => setAgentQ(e.target.value)} placeholder="Search Retell agents…" className="pl-9" disabled={!selected} data-testid="admin-voice-agent-search" />
          </div>
          <div className="divide-y divide-border max-h-80 overflow-y-auto" data-testid="admin-voice-agent-list">
            {agents === null ? (
              <div className="py-8 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : !selected ? (
              <p className="text-sm text-muted-foreground text-center py-8">Select a business to enable assignment.</p>
            ) : filteredAgents.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No agents match.</p>
            ) : filteredAgents.map((a) => {
              const isCurrent = selected.agent_id === a.agent_id;
              return (
                <div key={a.agent_id} className="flex items-center gap-3 py-2.5 px-2">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0"><PhoneCall className="w-4 h-4 text-muted-foreground" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{a.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{a.agent_id}</p>
                  </div>
                  <Button size="sm" variant={isCurrent ? "outline" : "default"} onClick={() => assign(a.agent_id)} disabled={busy || isCurrent} data-testid={`admin-voice-assign-${a.agent_id}`}>
                    {isCurrent ? <><Check className="w-4 h-4 mr-1" /> Assigned</> : <><Link2 className="w-4 h-4 mr-1" /> Assign</>}
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Current assignments */}
      <Card className="p-4 mt-5">
        <h3 className="font-medium text-sm mb-3">Current assignments ({assignments.length})</h3>
        {assignments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No businesses have a voice agent assigned yet.</p>
        ) : (
          <div className="divide-y divide-border" data-testid="admin-voice-assignments">
            {assignments.map((a) => (
              <div key={a.business_id} className="flex items-center gap-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.name} <span className="text-muted-foreground font-normal">· {a.email}</span></p>
                  <p className="text-[11px] text-muted-foreground truncate">{a.agent_name} <span className="opacity-60">({a.agent_id})</span></p>
                </div>
                <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive gap-1" onClick={() => unassign(a.business_id)} disabled={busy} data-testid={`admin-voice-remove-${a.business_id}`}>
                  <X className="w-4 h-4" /> Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminVoiceAgents;
